/**
 * ConfirmDialog - Diálogo de confirmación reutilizable
 * Para confirmar acciones importantes
 */
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';

/**
 * @component ConfirmDialog
 * @description Diálogo modal de confirmación
 * @param {Object} props
 * @param {boolean} props.open - Estado de apertura del diálogo
 * @param {string} props.title - Título del diálogo
 * @param {string} props.message - Mensaje de confirmación
 * @param {Function} props.onConfirm - Callback al confirmar
 * @param {Function} props.onCancel - Callback al cancelar
 * @param {string} [props.variant='default'] - Variante: default, danger
 * @param {string} [props.confirmText='Confirmar'] - Texto del botón de confirmar
 * @param {string} [props.cancelText='Cancelar'] - Texto del botón de cancelar
 * @param {boolean} [props.loading=false] - Estado de carga
 */
const ConfirmDialog = ({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  variant = 'default',
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  loading = false,
}) => {
  const isDanger = variant === 'danger';

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 3,
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          color: isDanger ? 'error.main' : 'text.primary',
        }}
      >
        {title}
      </DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ color: 'text.secondary' }}>
          {message}
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onCancel}
          disabled={loading}
          color="inherit"
          variant="outlined"
        >
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          color={isDanger ? 'error' : 'primary'}
          variant="contained"
          autoFocus
        >
          {loading ? 'Procesando...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['default', 'danger']),
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  loading: PropTypes.bool,
};

export default ConfirmDialog;
